import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import { getListMerchantsForSelect } from '../../../../common/core/_requests'
import { IsGranted, useAuth } from '../../../auth'
import { getPaymentAdapterPriorities } from '../../api/getPaymentAdapterPriorities'
import { getPaymentAdapterList } from '../../api/getPaymentAdapterList'
import { postAdapterPriorities } from '../../api/postAdapterPriorities'
import { postPriorityConfig } from '../../api/postPriorityConfig'
import { getAdapterRoutingCoverage } from '../../api/getAdapterRoutingCoverage'
import { deletePriorityConfig } from '../../api/deletePriorityConfig'
import { decodeContextId, encodeContextId } from './model/context'
import { computeConfigStatus } from './model/coverage'
import { diff } from './model/diff'
import {
  rowsFromApi,
  toPriorities,
  templateRows,
  type ApiAdapterRef,
  type ApiPriorityRow,
} from './model/projection'
import type { AdapterRow, RoutingContext } from './model/types'
import { AdapterTable } from './components/AdapterTable'
import { ValidationSummary } from './components/ValidationSummary'
import { SaveDialog } from './components/SaveDialog'
import { StatusBadge } from './components/StatusBadge'
import { FlagChip } from './components/FlagChip'
import { contextLabel } from './labels'
import { routingErrorText } from './errors'
import { ADAPTER_PRIORITY_WRITE_ROLES } from '../../core/adapterPriorityAccess'

const AppSwal = withReactContent(Swal)


const breadCrumbs: Array<PageLink> = [
  {
    title: 'Карта покрытия маршрутизации',
    path: '/settings/adapter-routing',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

/**
 * Экран B — Редактор конфигурации. Reads the real priority list + adapter
 * catalogue, lets the operator edit everything on view (drag rank, active,
 * min/max, traffic split), validate live, simulate routing, diff and save via
 * postAdapterPriorities. Returns to the map via breadcrumb / back button.
 */
const ConfigEditorPage = () => {
  const { contextId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const wantsTemplate = searchParams.get('template') === '1'
  const { currentUser } = useAuth()
  // the merchant payments operator reads the config; saving, enabling and deleting it is refused by the backend
  const canWrite = IsGranted(ADAPTER_PRIORITY_WRITE_ROLES, currentUser)

  const context = useMemo<RoutingContext | null>(() => decodeContextId(contextId), [contextId])

  const [rows, setRows] = useState<AdapterRow[]>([])
  const [baseline, setBaseline] = useState<AdapterRow[]>([])
  const [catalogue, setCatalogue] = useState<ApiAdapterRef[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [saveOpen, setSaveOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  /**
   * CHC-876: шапка знает только merchantToken (из URL). Резолвим его в
   * человекочитаемое название через тот же боевой сервис, что и карта покрытия.
   * Фолбэк — сам токен (пока грузится или если мерчант не найден).
   */
  const [merchantTitle, setMerchantTitle] = useState<string | null>(null)

  // CPAYMENT-1485: configuration-level «Выключена» flag (meta-config). Loaded
  // from the coverage endpoint so the toggle reflects the real state, and
  // toggled here — the only UI control for enabling/disabling a configuration.
  const [configEnabled, setConfigEnabled] = useState<boolean>(true)
  const [enabledSaving, setEnabledSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!context) return
    let alive = true
    getListMerchantsForSelect(currentUser)
      .then(({ data }) => {
        if (!alive || !data || !data.success || !Array.isArray(data.merchants)) return
        const match = data.merchants.find((m) => m.token === context.merchantToken)
        if (match && match.title) setMerchantTitle(match.title)
      })
      .catch(() => {
        /* фолбэк на токен — название не критично для работы редактора */
      })
    return () => {
      alive = false
    }
  }, [context, currentUser])

  // Load the real configuration-level enabled flag for this context.
  useEffect(() => {
    if (!context) return
    let alive = true
    getAdapterRoutingCoverage(context.merchantToken, currentUser)
      .then((res) => {
        if (!alive) return
        const id = encodeContextId(context)
        const match = res.configurations.find((c) => encodeContextId(c.context) === id)
        if (match) setConfigEnabled(match.enabled)
      })
      .catch(() => {
        /* keep default enabled=true; toggling still persists the chosen value */
      })
    return () => {
      alive = false
    }
  }, [context, currentUser])

  useEffect(() => {
    if (!context) {
      setIsLoading(false)
      return
    }
    let alive = true
    setIsLoading(true)

    const load = async () => {
      try {
        const { data: listData } = await getPaymentAdapterList()
        const cat: ApiAdapterRef[] =
          listData && listData.success ? (listData.paymentAdapters as ApiAdapterRef[]) : []

        const { data } = await getPaymentAdapterPriorities({
          merchantToken: context.merchantToken,
          paymentMethodType: context.paymentMethodType,
          currency: context.currency,
          direction: context.direction,
          externalUserFlag: context.externalUserFlag,
          source: context.source,
        })

        if (!alive) return

        const priorities: ApiPriorityRow[] =
          data && data.success ? (data.paymentAdapterPriorities as ApiPriorityRow[]) : []

        setCatalogue(cat)

        let next: AdapterRow[]
        if (priorities.length === 0 && wantsTemplate) {
          next = templateRows(cat, context.source)
        } else {
          next = rowsFromApi(priorities, cat, context.source)
        }
        setRows(next)
        setBaseline(next.map((r) => ({ ...r })))
      } catch (err) {
        if (alive)
          AppSwal.fire({ title: 'Не удалось загрузить конфигурацию', text: routingErrorText(err), icon: 'error' })
      } finally {
        if (alive) setIsLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [context, wantsTemplate])

  const dirty = useMemo(() => diff(baseline, rows).length > 0, [baseline, rows])
  const status = useMemo(() => computeConfigStatus(rows), [rows])

  const reset = () => {
    setRows(baseline.map((r) => ({ ...r })))
    AppSwal.fire({ title: 'Изменения сброшены', timer: 1200, showConfirmButton: false })
  }

  const fillFromTemplate = () => {
    setRows(templateRows(catalogue, context!.source))
    AppSwal.fire({
      title: 'Заполнено из шаблона — отредактируйте и сохраните',
      timer: 1500,
      showConfirmButton: false,
    })
  }

  const confirmSave = async () => {
    if (!context) return
    setSaving(true)
    try {
      const { data } = await postAdapterPriorities({
        priorities: toPriorities(rows, context.source),
        merchantToken: context.merchantToken,
        paymentMethodType: context.paymentMethodType,
        currency: context.currency,
        direction: context.direction,
        externalUserFlag: context.externalUserFlag,
      })

      if (data && data.success && !data.errors) {
        setBaseline(rows.map((r) => ({ ...r })))
        setSaveOpen(false)
        AppSwal.fire({
          title: 'Конфигурация маршрутизации сохранена!',
          timer: 1500,
          showConfirmButton: false,
        })
      } else {
        AppSwal.fire({ title: 'Сервер отклонил сохранение', icon: 'error' })
      }
    } catch (err) {
      AppSwal.fire({ title: 'Не удалось сохранить конфигурацию', text: routingErrorText(err), icon: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // CPAYMENT-1485: enable/disable the whole configuration (meta-config «Выключена»).
  // Persists immediately via POST /priority/config (order is left untouched —
  // the backend upsert only writes the provided fields). Reverts on failure.
  const toggleEnabled = async (nextEnabled: boolean) => {
    if (!context) return
    const prev = configEnabled
    setEnabledSaving(true)
    setConfigEnabled(nextEnabled)
    try {
      const { data } = await postPriorityConfig({
        merchantToken: context.merchantToken,
        paymentMethodType: context.paymentMethodType,
        currency: context.currency,
        direction: context.direction,
        externalUserFlag: context.externalUserFlag,
        source: context.source,
        enabled: nextEnabled,
      })
      if (!data || !data.success) throw new Error('rejected')
      AppSwal.fire({
        title: nextEnabled ? 'Конфигурация включена' : 'Конфигурация выключена',
        timer: 1300,
        showConfirmButton: false,
      })
    } catch (err) {
      setConfigEnabled(prev)
      AppSwal.fire({
        title: 'Не удалось изменить статус конфигурации',
        text: routingErrorText(err),
        icon: 'error',
      })
    } finally {
      setEnabledSaving(false)
    }
  }

  // Удаление конфигурации сносит сам контекст — строки приоритетов, из которых
  // собрана карточка карты покрытия. Подтверждение говорит об этом прямо;
  // «Сбросить» по-прежнему откатывает только несохранённые правки в редакторе.
  const removeConfig = async () => {
    if (!context) return
    const confirmed = await AppSwal.fire({
      title: 'Удалить конфигурацию?',
      text: 'Будут удалены все адаптеры этого контекста и сама карточка. Действие необратимо.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
      confirmButtonColor: '#d9214e',
    })
    if (!confirmed.isConfirmed) return

    setDeleting(true)
    try {
      const { data } = await deletePriorityConfig({
        merchantToken: context.merchantToken,
        paymentMethodType: context.paymentMethodType,
        currency: context.currency,
        direction: context.direction,
        externalUserFlag: context.externalUserFlag,
        source: context.source,
      })
      if (!data || !data.success) throw new Error('rejected')
      navigate(`/settings/adapter-routing?merchant=${encodeURIComponent(context.merchantToken)}`)
    } catch (err) {
      AppSwal.fire({
        title: 'Не удалось удалить конфигурацию',
        text: routingErrorText(err),
        icon: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  if (!context) {
    return (
      <div className='card'>
        <div className='card-body text-center py-15'>
          <div className='fs-4 fw-bold text-gray-800 mb-3'>
            Неверный идентификатор контекста.
          </div>
          <Link to='/settings/adapter-routing' className='btn btn-light-primary'>
            Вернуться к карте
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageTitle breadcrumbs={breadCrumbs}>{'Редактор конфигурации'}</PageTitle>

      <div className='card mb-7'>
        <div className='card-body d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <div className='d-flex align-items-center gap-3 mb-1'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() =>
                  navigate(`/settings/adapter-routing?merchant=${encodeURIComponent(context.merchantToken)}`)
                }
              >
                <KTIcon iconName='arrow-left' className='fs-4 me-1' />
                К карте покрытия
              </button>
              {rows.length > 0 && <StatusBadge status={status} />}
              <FlagChip flag={context.externalUserFlag} />
            </div>
            <span className='fw-bold fs-3 text-gray-900'>
              {merchantTitle ?? context.merchantToken}
            </span>
            <span className='text-gray-600 fs-7'>{contextLabel(context)}</span>
          </div>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center me-2'>
              <input
                type='checkbox'
                className='form-check-input h-25px w-45px'
                style={{ cursor: enabledSaving ? 'not-allowed' : 'pointer' }}
                checked={configEnabled}
                disabled={enabledSaving || !canWrite}
                onChange={(e) => toggleEnabled(e.target.checked)}
              />
              <span className='form-check-label fw-semibold text-gray-700 ms-2'>
                {configEnabled ? 'Конфигурация включена' : 'Конфигурация выключена — маршрут не работает'}
              </span>
            </label>
            {canWrite && rows.length === 0 && (
              <button type='button' className='btn btn-light-primary' onClick={fillFromTemplate}>
                <KTIcon iconName='abstract-26' className='fs-4 me-1' />
                Заполнить из шаблона
              </button>
            )}
            {canWrite && (
              <>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={reset}
                  disabled={!dirty || saving}
                >
                  Сбросить
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={() => setSaveOpen(true)}
                  disabled={!dirty || saving}
                >
                  Сохранить
                </button>
                <button
                  type='button'
                  className='btn btn-light-danger'
                  onClick={removeConfig}
                  disabled={deleting || saving}
                >
                  <KTIcon iconName='trash' className='fs-4 me-1' />
                  Удалить
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='card'>
          <div className='card-body text-gray-600 py-10'>Загрузка конфигурации…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className='card'>
          <div className='card-body text-center py-15'>
            <KTIcon iconName='abstract-26' className='fs-3x text-gray-400 mb-4' />
            <div className='fs-4 fw-bold text-gray-800 mb-2'>Контекст не настроен</div>
            <div className='text-gray-600 mb-5 mx-auto' style={{ maxWidth: 460 }}>
              Здесь нет ни одного адаптера. Заполните из шаблона, чтобы не собирать список с нуля,
              затем отредактируйте диапазоны и приоритеты.
            </div>
            {canWrite && (
              <button type='button' className='btn btn-primary' onClick={fillFromTemplate}>
                <KTIcon iconName='abstract-26' className='fs-4 me-1' />
                Заполнить из шаблона
              </button>
            )}
          </div>
        </div>
      ) : (
        // CHC-872: «Живая симуляция» убрана — редактор в одну колонку. Сигнал о
        // покрытии (дыры/пересечения/все выключены/конфликт) остаётся инлайн в
        // ValidationSummary под таблицей, не загромождая экран.
        <div className='card mb-7'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Адаптеры</h3>
          </div>
          <div className='card-body pt-2'>
            <AdapterTable rows={rows} setRows={setRows} readOnly={!canWrite} />
            <div className='mt-5'>
              <ValidationSummary rows={rows} />
            </div>
          </div>
        </div>
      )}

      <SaveDialog
        show={saveOpen}
        onClose={() => setSaveOpen(false)}
        before={baseline}
        after={rows}
        saving={saving}
        onConfirm={confirmSave}
      />
    </div>
  )
}

export default ConfigEditorPage
